import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomList from '../../../../../../core/components/core/Util/List/CustomList/CustomList';
import CustomListContainer from '../../../../../../core/components/core/Util/List/CustomList/CustomListContainer';
import CustomCheckBox from '../../../../../../core/components/core/Util/Form/CustomInputs/CustomCheckBox';
import Toast from '../../../../../../core/components/core/Util/Toast/Toast';
import Fetch from '../../../../../../core/util/core/Fetch';
import { VerifyPermission } from '../../../../../../core/util/core/VerifyPermission';
import config from '../../../../../config/config';
import { selectionProcessStatusCell } from '../../../../../helpers/selectionProcessStatusCell';
import { subscriptionStatusCell } from '../../../../../helpers/subscriptionStatusCell';

const SELECTED_KEY = 'psp_selected_subscriptions';
const ENDPOINT = `${config.endpoints.backend}psp/subscriptions/getall`;
const ROWS_KEY = `custom_list_data_${ENDPOINT}`;
// CustomCheckBox renders the input as an inline sibling of its label text, which baseline-aligns
// them by default; verticalAlign keeps the checkbox centered with the text next to it.
const CHECKBOX_STYLE = { position: 'static', margin: 0, verticalAlign: 'middle' };

const useSelectedSubscriptions = () => {
    const dispatch = useDispatch();
    const selected = useSelector((state) => state.core.session[SELECTED_KEY] || []);
    const setSelected = (value) => dispatch({ type: 'UPDATE_SESSION', key: SELECTED_KEY, value });
    return [selected, setSelected];
};

// Reads the selection from the session because CustomList freezes `columnsButtons` checkboxes
// at load time, which would make "select all" refetch the list on every click.
function SelectCandidate({ row }) {
    const [selected, setSelected] = useSelectedSubscriptions();

    return (
        <CustomCheckBox
            name={`subscription_${row.id}`}
            label={<span className="sr-only">Selecionar {row.social_name || row.name}</span>}
            checked={selected.includes(row.id)}
            style={CHECKBOX_STYLE}
            onChange={(element) => setSelected(
                element.target.checked ? [...selected, row.id] : selected.filter((id) => id !== row.id)
            )}
        />
    );
}

function SelectionBar() {
    const [selected, setSelected] = useSelectedSubscriptions();
    const rows = useSelector((state) => state.core.session[ROWS_KEY] || []);

    return (
        <div className="d-flex align-items-center px-3 pt-3 pb-2 border-bottom">
            <CustomCheckBox
                name="select_all_candidates"
                label={<span className="ml-2 font-weight-normal" style={{ verticalAlign: 'middle' }}>Selecionar todos os candidatos</span>}
                checked={rows.length > 0 && selected.length === rows.length}
                style={CHECKBOX_STYLE}
                onChange={(element) => setSelected(element.target.checked ? rows.map((row) => row.id) : [])}
            />
            {selected.length > 0 && (
                <span className="badge badge-primary ml-3" style={{ verticalAlign: 'middle' }}>
                    {selected.length} {selected.length > 1 ? 'selecionados' : 'selecionado'}
                </span>
            )}
        </div>
    );
}

const candidateCell = (row) => (
    <div>
        <strong>{row.social_name || row.name}</strong>
        <div className="small text-muted">RG: {row.rg} | {row.email}</div>
    </div>
);

const Info = ({ label, children }) => (
    <div className="col-md-4 mb-3">
        <small className="text-muted d-block">{label}</small>
        {children || '-'}
    </div>
);

function ProcessHeader({ selectionProcessId, vacancyId }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const process = await Fetch.post(`${config.endpoints.backend}psp/selectionprocesses/getById`, { id: selectionProcessId });
                setData({
                    process,
                    vacancy: (process.vacancies || []).find((item) => item.id === vacancyId) || {}
                });
            } catch (e) {
                console.error('[psp/candidates] Erro ao carregar o cabeçalho:', e);
                Toast.error('Não foi possível carregar os dados do processo seletivo.');
            }
        };
        load();
    }, [selectionProcessId, vacancyId]);

    if (!data) return null;

    const { process, vacancy } = data;

    return (
        <div className="card">
            <div className="card-body pb-0">
                <h5 className="mb-0">Processo Seletivo: {process.title}</h5>
                {process.edital_number && <div className="small text-muted">Edital: {process.edital_number}</div>}
                <div className="mt-2">{selectionProcessStatusCell(process)}</div>
                <hr />
                <h5 className="mb-3">Vaga: {vacancy.title || '-'}</h5>
                <div className="row">
                    <Info label="Função / Cargo">{vacancy.role_name}</Info>
                    <Info label="Escolaridade Exigida">{vacancy.academic_background}</Info>
                    <Info label="Prova">{vacancy.has_exam ? vacancy.exam_type || 'Sim' : 'Não'}</Info>
                </div>
            </div>
        </div>
    );
}

function List() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectionProcessId, vacancyId } = useParams();
    const pathVacancies = `/psp-selectionprocesses/${selectionProcessId}/vacancies/`;
    const { canView } = VerifyPermission();

    useEffect(() => () => {
        dispatch({ type: 'UPDATE_SESSION', key: SELECTED_KEY, value: [] });
    }, [dispatch]);

    const list = {
        endpoint: ENDPOINT,
        filter: { psp_selection_process_vacancy_id: vacancyId },
        canEdit: false,
        canDelete: false,
        viewActionColumn: false,
        columns: [
            {
                name: 'selected',
                label: '',
                sortable: false,
                width: '6%',
                cell: (row) => <SelectCandidate row={row} />
            },
            { name: 'name', label: 'Candidato', width: '25%', cell: candidateCell },
            { name: 'subscription_number', label: 'Nº Inscrição', width: '15%' },
            { name: 'schooling', label: 'Escolaridade', width: '14%' },
            { name: 'subscription_date', label: 'Data de Inscrição', width: '12%' },
            { name: 'status', label: 'Status', width: '15%', cell: subscriptionStatusCell }
        ],
        columnsButtons: [
            {
                buttons: [
                    {
                        type: 'listButton',
                        label: 'Ações',
                        icon: 'fas fa-cog',
                        buttonClass: 'btn btn-outline-primary btn-sm px-3',
                        showActionButtons: false,
                        width: '8%',
                        buttons: [
                            {
                                label: 'Visualização de Dados',
                                icon: 'fas fa-address-card',
                                hideListButtonRule: () => !canView,
                                onClick: (item) => navigate(`${pathVacancies}${vacancyId}/candidates/view/${item.id}`)
                            }
                        ]
                    }
                ]
            }
        ]
    };

    const buttons = [
        {
            name: 'back_to_vacancies',
            label: 'Vagas',
            icon: 'fas fa-arrow-left',
            class: 'btn btn-outline-secondary',
            tooltipLabel: 'Voltar para listagem de Vagas',
            callback: () => navigate(`${pathVacancies}list`)
        }
    ];

    return (
        <div>
            <ProcessHeader selectionProcessId={selectionProcessId} vacancyId={vacancyId} />
            <CustomListContainer
                canAdd={false}
                labelTitle="Candidatos Inscritos"
                buttons={buttons}
                // Not in list.customHeader: listButton puts `list` in each row and CustomList
                // stringifies the rows, so a React element inside `list` crashes with a circular JSON.
                content={<><SelectionBar /><CustomList list={list} /></>}
            />
        </div>
    );
}

export default List;
